/**
 * SKY BANDS — the sky as flat cel bands (hard edges, gently wavy) instead of a smooth ramp.
 *
 * The colour of each band is the palette ramp (top → mid → bottom, render/sky/sky-utils.js)
 * sampled in the band; the lowest band is the palette's pale horizon haze. Band edges sit at
 * fixed fractions of the sky band (canvas top → sea line) and get narrower toward the horizon,
 * which reads as depth. The GPU sky shader receives these same colours and edges as uniforms
 * (render/sky/shader-uniforms.js), so the two renderers match by construction.
 */

import {
  SKY_KEYFRAMES,
  sampleKeyframes,
  rgb,
  lerpRgb,
  clamp01,
  normalizeTimeOfDay,
} from "./sky-utils.js";

/** Lower edge of each band but the last, as a fraction of the sky band (0 top, 1 sea line). */
export var SKY_BAND_EDGES = [0.2, 0.36, 0.5, 0.62, 0.72, 0.8, 0.87, 0.93];
export var SKY_BAND_COUNT = SKY_BAND_EDGES.length + 1;
/** Wave on each band edge: amplitude and wavelength in px at scaleUi 1. */
export var SKY_BAND_WAVE_AT_720 = 3;
export var SKY_BAND_WAVELENGTH_AT_720 = 240;

function ramp(top, mid, bottom, f) {
  if (f <= 0.5) return lerpRgb(top, mid, f / 0.5);
  return lerpRgb(mid, bottom, (f - 0.5) / 0.5);
}

/**
 * Flat colour of every band for a time of day, top band first.
 * @param {number} timeOfDay 0..1 colour phase
 * @returns {number[][]} SKY_BAND_COUNT [r, g, b] colours
 */
export function skyBandColors(timeOfDay) {
  var t = normalizeTimeOfDay(timeOfDay);
  var top = sampleKeyframes(SKY_KEYFRAMES, t, "top");
  var mid = sampleKeyframes(SKY_KEYFRAMES, t, "mid");
  var bottom = sampleKeyframes(SKY_KEYFRAMES, t, "bottom");
  var haze = sampleKeyframes(SKY_KEYFRAMES, t, "haze");
  var out = [];
  for (var k = 0; k < SKY_BAND_COUNT; k++) {
    var lo = k === 0 ? 0 : SKY_BAND_EDGES[k - 1];
    var hi = k < SKY_BAND_EDGES.length ? SKY_BAND_EDGES[k] : 1;
    if (k === SKY_BAND_COUNT - 1) {
      out.push(haze);
    } else {
      // Sample a little above the middle so the top band keeps the full deep tone.
      out.push(ramp(top, mid, bottom, lo + (hi - lo) * 0.35));
    }
  }
  return out;
}

/** Band index (0 = top) at a fraction of the sky band, ignoring the edge wave. */
export function skyBandIndex(yFrac) {
  var f = clamp01(yFrac);
  for (var k = 0; k < SKY_BAND_EDGES.length; k++) {
    if (f < SKY_BAND_EDGES[k]) return k;
  }
  return SKY_BAND_EDGES.length;
}

/**
 * Vertical offset of band edge `k` at x (px). The GPU shader uses the same formula.
 * @param {number} k edge index
 * @param {number} x px from the left of the canvas
 * @param {number} scaleUi
 */
export function skyBandEdgeWave(k, x, scaleUi) {
  var s = Math.max(0.05, Number(scaleUi) || 1);
  var amp = SKY_BAND_WAVE_AT_720 * s;
  var len = SKY_BAND_WAVELENGTH_AT_720 * s * (1 + 0.15 * k);
  return amp * Math.sin((x / len) * Math.PI * 2 + k * 1.7);
}

/**
 * Sky colour at a vertical fraction of the sky band (0 = top, 1 = sea line): the flat band
 * colour there, exactly what drawSky paints (minus the edge wave). Lets other layers (the
 * moon's dark side) take the sky behind them.
 * @returns {number[]} [r, g, b]
 */
export function skyColorAt(timeOfDay, yFrac) {
  return skyBandColors(timeOfDay)[skyBandIndex(yFrac)].slice();
}

/**
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ x: number, y: number, width: number, height: number }} bounds
 * @param {number} timeOfDay
 * @param {{ seaLineY?: number, scaleUi?: number }} [opts]
 */
export function drawSky(ctx, bounds, timeOfDay, opts) {
  if (!ctx || !bounds) return;
  opts = opts || {};
  var x = bounds.x;
  var y = bounds.y;
  var w = Math.max(0, bounds.width);
  var h = Math.max(0, bounds.height);
  if (w <= 0 || h <= 0) return;
  var seaLineY = Number(opts.seaLineY);
  var skyH = isFinite(seaLineY) && seaLineY > y ? seaLineY - y : h;
  var scaleUi = Number(opts.scaleUi);
  if (!(scaleUi > 0)) scaleUi = Math.max(0.5, w / 720);
  var colors = skyBandColors(timeOfDay);

  // Bottom band first over everything, then each band above paints down to its wavy edge.
  ctx.fillStyle = rgb(colors[colors.length - 1]);
  ctx.fillRect(x, y, w, h);
  var step = Math.max(6, 24 * scaleUi);
  for (var k = SKY_BAND_EDGES.length - 1; k >= 0; k--) {
    var edgeY = y + SKY_BAND_EDGES[k] * skyH;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + w, y);
    var px = w;
    ctx.lineTo(x + px, edgeY + skyBandEdgeWave(k, px, scaleUi));
    while (px > 0) {
      px = Math.max(0, px - step);
      ctx.lineTo(x + px, edgeY + skyBandEdgeWave(k, px, scaleUi));
    }
    ctx.closePath();
    ctx.fillStyle = rgb(colors[k]);
    ctx.fill();
  }
}
